//@flow
import React from 'react';
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import ConfirmDialog from "../../../../../components/app/dialogs/confirmDialog/confirmDialog";
import FileUpload from "../../../../../components/form/fileUpload/fileUpload";
import Button from "@mui/material/Button";
import DialogContentText from "@mui/material/DialogContentText";
import { FormikProvider, useFormik } from "formik";
import Box from "@mui/material/Box";
import * as yup from "yup";
import { Trans, useTranslation } from "react-i18next";
import { dialogueContextTextDefaults } from "../../../../../helpers/theme/defaultProps";
import useExhibitionAction from "../../../../../hooks/exhibitions/useExhibitionAction";
import useExhibitionData from "../../../../../hooks/exhibitions/useExhibitionData";
import BulkImportStatus from "../../../../../components/app/alerts/bulkImportStatus/bulkImportStatus";
import { ACCEPT_EXCEL, API, PartnerTypes } from "../../../../../helpers/constants";
import { normalizeGreek } from "../../../../../helpers/normalizeGreek";
import { AuthenticatedLink } from "../../../../../components/general/authenticatedLink/authenticatedLink";
import { EXHIBITION, EXHIBITOR_PROFILE } from "../../../../../locales/components/namespaces";

type Props = {
	exhibitionId: string,
	exhibitorId?: string,
	show: boolean,
	onClose: Function,
}

const ExcelImport = (props: Props) => {
	const { show, onClose, exhibitionId, exhibitorId, type } = props
	const { t } = useTranslation(EXHIBITION)
	const { createBulkPartners, clearBulkPartnersStatus } = useExhibitionAction()
	const { bulkImporting, partnerBulkImportStatus } = useExhibitionData()
	const onSubmit = (values, { resetForm }) => {
		createBulkPartners(exhibitionId, exhibitorId, values, type)
		resetForm()
	}
	const validationSchema = yup.object({
		files: yup
		.array()
		.min(1)
		.required(t("at least one item needs to be here"))
	})
	const formik = useFormik({
		initialValues: {
			files: []
		},
		validationSchema: validationSchema,
		onSubmit: onSubmit,
	})
	const onFileChange = (val) => {
		formik.setFieldValue('files', val)
	}
	const closeDialogue = () => {
		formik.setFieldValue('files', [])
		onClose()
	}
	return (
		<Box>
			<ConfirmDialog
				maxWidth={"md"}
				loading={bulkImporting}
				open={show}
				handleDialog={closeDialogue}
				title={t("Import from excel")}
				otherDialogProps={{ keepMounted: true }}
				yesBtnDisabled={!(formik.isValid && formik.dirty)}
				yesBtnText={t('Import selected file')}
				onNo={closeDialogue}
				onYes={formik.handleSubmit}
				yesBtnVariant={'outlined'}
				hasBtnIcons={false}
			>
				<FormikProvider value={formik}>
					<Box
						sx={{
							'& .MuiTextField-root': { my: 1 },
						}}
						component="form"
						onSubmit={formik.handleSubmit}
					>
						<BulkImportStatus
							onClose={clearBulkPartnersStatus}
							status={partnerBulkImportStatus}
						/>
						<DialogContentText
							{...dialogueContextTextDefaults}
						>
							<Trans
								i18nKey={'import_excel_msg'}
								ns={EXHIBITOR_PROFILE}
							>
								If you want to add more than one people quickly you can upload
								an <strong>Excel</strong> file.<br/>
								<ul>
									<li>Your excel file should follow the structure described bellow.</li>
									<li>Make sure your columns are in order as in the example table.</li>
									<li>For your convenience, you you also download our blank excel template.
									</li>
								</ul>
							</Trans>
						</DialogContentText>
						<TableContainer
							component={Paper}
							variant={"body2"}
							sx={{
								mb: 1,
								border: '1px solid #F6F6F6',
								borderRadius: 0
							}}
						>
							<Table sx={{ minWidth: 650, background: '#F6F6F6', }} size={"small"}>
								<TableHead>
									<TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
										<TableCell>Name EL</TableCell>
										<TableCell>Name EN</TableCell>
										<TableCell>Email</TableCell>
										{(props.type === PartnerTypes.ServiceProvider || props.type === PartnerTypes.ExhibitionStaff || props.type === PartnerTypes.Contractor) &&
											<TableCell>Company</TableCell>}
									</TableRow>
								</TableHead>
								<TableBody>
									<TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
										<TableCell>ΓΙΑΝΝΗΣ ΝΤΟ</TableCell>
										<TableCell>JOHN DOE</TableCell>
										<TableCell>someone@email.com</TableCell>
										{(props.type === PartnerTypes.ServiceProvider || props.type === PartnerTypes.ExhibitionStaff || props.type === PartnerTypes.Contractor) &&
											<TableCell>ROTA</TableCell>}
									</TableRow>
								</TableBody>
							</Table>
						</TableContainer>
						<AuthenticatedLink
							url={API + "/en/partner/export"}
							filename={normalizeGreek(t('Download excel template'))}
						>
							<Button
								sx={{ mb: 3, width: '100%', justifyContent: 'flex-end' }}
								size={"small"}
								component={"button"}
								color={'highlight'}
							>
								{t('Download excel template')}
							</Button>
						</AuthenticatedLink>
						<FileUpload
							accept={{ [ACCEPT_EXCEL]: ['.xlsx'] }}
							multiple={false}
							onRemove={onFileChange}
							onFilesUpload={onFileChange}
						/>
					</Box>
				</FormikProvider>
			</ConfirmDialog>
		</Box>
	)
}

export default ExcelImport;